import { SUPPORT_HINT } from '../support-contact';

/**
 * The API's error codes this application branches on — a subset of the backend's `ErrorCode` enum.
 *
 * Every error response carries a `code` and a Czech `detail`; the detail is what the user reads, so
 * a code only needs to be listed here when something *decides* on it: closing a dialog, logging
 * out, retrying, routing to a different state. A code the backend adds later is shown through its
 * detail without any change here.
 */
export const ApiErrorCode = {
  // Transport and framework
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  NOT_FOUND: 'NOT_FOUND',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  EMAIL_SEND_FAILED: 'EMAIL_SEND_FAILED',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  AI_RESPONSE_FAILED: 'AI_RESPONSE_FAILED',

  // Authentication and authorization
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  WRONG_CURRENT_PASSWORD: 'WRONG_CURRENT_PASSWORD',
  FORBIDDEN: 'FORBIDDEN',

  // License, credit and lock gates
  SCHOOL_LICENSE_INACTIVE: 'SCHOOL_LICENSE_INACTIVE',
  LICENSE_TIER_INSUFFICIENT: 'LICENSE_TIER_INSUFFICIENT',
  AI_QUOTA_EXCEEDED: 'AI_QUOTA_EXCEEDED',
  SVP_LOCKED: 'SVP_LOCKED',
  LICENSE_PERIOD_OVERLAP: 'LICENSE_PERIOD_OVERLAP',
  LICENSE_ALREADY_REVOKED: 'LICENSE_ALREADY_REVOKED',
  NO_ACTIVE_LICENSE: 'NO_ACTIVE_LICENSE',

  // Accounts and membership
  EMAIL_ALREADY_USED: 'EMAIL_ALREADY_USED',
  EMAIL_UNCHANGED: 'EMAIL_UNCHANGED',
  NOT_SCHOOL_MEMBER: 'NOT_SCHOOL_MEMBER',
  SCHOOL_MESSAGE_RECIPIENT_NOT_MEMBER: 'SCHOOL_MESSAGE_RECIPIENT_NOT_MEMBER',
  INVITATION_INVALID: 'INVITATION_INVALID',
  INVITATION_EXPIRED: 'INVITATION_EXPIRED',
  INVITATION_FOR_OTHER_USER: 'INVITATION_FOR_OTHER_USER',
  INVITATION_SEND_FAILED: 'INVITATION_SEND_FAILED',
  PASSWORD_TOKEN_INVALID: 'PASSWORD_TOKEN_INVALID',
  PASSWORD_TOKEN_EXPIRED: 'PASSWORD_TOKEN_EXPIRED',
  EMAIL_CHANGE_TOKEN_INVALID: 'EMAIL_CHANGE_TOKEN_INVALID',
  EMAIL_CHANGE_TOKEN_EXPIRED: 'EMAIL_CHANGE_TOKEN_EXPIRED',
  EMAIL_CHANGE_FOR_OTHER_USER: 'EMAIL_CHANGE_FOR_OTHER_USER',

  // Curriculum data
  SVP_NAME_TAKEN: 'SVP_NAME_TAKEN',
  SUBJECT_HAS_NO_SOVU: 'SUBJECT_HAS_NO_SOVU',
  EXCEL_IMPORT_ERROR: 'EXCEL_IMPORT_ERROR',
  SMILE_IMPORT_ERROR: 'SMILE_IMPORT_ERROR',
  DIARY_ENTRY_NOT_EDITABLE: 'DIARY_ENTRY_NOT_EDITABLE',
} as const;

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

/** Shown when the API explained nothing — no body, no code, or an `INTERNAL_ERROR` whose detail is not trusted. */
export const UNKNOWN_ERROR_MESSAGE = `Došlo k neočekávané chybě. Zkuste to prosím znovu. ${SUPPORT_HINT}`;
